import type { SoundName, Direction, BLSState, MixedModeConfig, SubscriptionTier } from '../../types';
import { CollapsibleSection } from './CollapsibleSection';
import { ColorPicker } from '../ColorPicker';
import { DirectionPicker } from '../DirectionPicker';
import { SoundPicker } from '../SoundPicker';
import { VolumeControl } from '../VolumeControl';
import { BackgroundPicker } from '../controls/BackgroundPicker';
import { MovementStylePicker } from '../controls/MovementStylePicker';
import { ShapePicker } from '../controls/ShapePicker';
import { MixedModeConfigSection } from '../controls/MixedModeConfigSection';

/**
 * AdvancedLayout - Full-featured control layout with all options
 *
 * Features:
 * - All 8 colors + custom picker
 * - All 5 directions
 * - All 11 sounds with mixing capability
 * - Background, movement style, chaos mode, shape controls
 * - Collapsible sections for better organization
 */

interface AdvancedLayoutProps {
  state: BLSState;
  tier: SubscriptionTier;
  isPro: boolean;
  onColorChange: (color: string) => void;
  onDirectionChange: (direction: Direction) => void;
  onSoundChange: (sound: SoundName) => void;
  onVolumeChange: (volume: number) => void;
  onMuteChange: (muted: boolean) => void;
  onMultiSelectChange?: (enabled: boolean) => void;
  onBackgroundChange: (background: string) => void;
  onMovementStyleChange: (style: BLSState['movementStyle']) => void;
  onShapeChange: (shape: BLSState['shape']) => void;
  onVisualEnabledChange?: (enabled: boolean) => void;
  onAudioEnabledChange?: (enabled: boolean) => void;
  onToggleAllRandom: (enabled: boolean) => void;
  onMixedModeConfigChange: (config: Partial<MixedModeConfig>) => void;
  onMixedModeChange: (enabled: boolean) => void;
  canUseMixedModeConfig: (param: keyof MixedModeConfig) => boolean;
}

export function AdvancedLayout({
  state,
  tier,
  isPro,
  onColorChange,
  onDirectionChange,
  onSoundChange,
  onVolumeChange,
  onMuteChange,
  onMultiSelectChange,
  onBackgroundChange,
  onMovementStyleChange,
  onShapeChange,
  onVisualEnabledChange,
  onAudioEnabledChange,
  onToggleAllRandom,
  onMixedModeConfigChange,
  onMixedModeChange,
  canUseMixedModeConfig,
}: AdvancedLayoutProps): JSX.Element {
  return (
    <div className="space-y-4">
      {/* Visual Settings Section */}
      <CollapsibleSection
        title="Visual Settings"
        defaultOpen
        icon="🎨"
        toggleEnabled={state.visualEnabled}
        onToggleChange={onVisualEnabledChange}
      >
        <ColorPicker
          value={state.color}
          onChange={onColorChange}
          disabled={false}
          isPro={isPro}
          mode="advanced"
        />

        <BackgroundPicker
          value={state.background}
          onChange={onBackgroundChange}
          disabled={false}
          isPro={isPro}
        />

        <ShapePicker
          value={state.shape}
          onChange={onShapeChange}
          disabled={false}
          isPro={isPro}
        />

        <DirectionPicker
          value={state.direction}
          onChange={onDirectionChange}
          disabled={false}
          isPro={isPro}
          mode="advanced"
        />

        <MovementStylePicker
          value={state.movementStyle}
          onChange={onMovementStyleChange}
          disabled={false}
          isPro={isPro}
        />

      </CollapsibleSection>

      {/* Audio Section */}
      <CollapsibleSection
        title="Audio"
        icon="🔊"
        toggleEnabled={state.audioEnabled}
        onToggleChange={onAudioEnabledChange}
      >
        <SoundPicker
          value={state.selectedSounds[0] || 'tap'}
          values={state.selectedSounds}
          onChange={onSoundChange}
          multiSelect={state.audioMultiSelect}
          disabled={false}
          isPro={isPro}
          mode="advanced"
        />

        <VolumeControl
          value={state.volume}
          onChange={onVolumeChange}
          disabled={false}
          tier={tier}
        />

        <div className="pt-2">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={state.audioMultiSelect}
              onChange={(e) => {
                if (onMultiSelectChange) {
                  onMultiSelectChange(e.target.checked);
                }
              }}
              className="w-5 h-5 text-sync-blue rounded"
            />
            <div className="flex-1">
              <span className="text-sm font-medium">Multi-Select Sounds</span>
              <p className="text-xs text-gray-500">Play multiple sounds simultaneously</p>
            </div>
          </label>
        </div>

        <div className="pt-2">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={state.muteTherapistAudio}
              onChange={(e) => onMuteChange(e.target.checked)}
              className="w-5 h-5 text-sync-blue rounded"
            />
            <span className="text-sm font-medium">Mute BLS audio for me</span>
          </label>
        </div>
      </CollapsibleSection>

      {/* Mixed Mode Configuration Section */}
      <CollapsibleSection
        title="Mixed Mode Configuration"
        icon="🎲"
        defaultOpen={false}
      >
        <MixedModeConfigSection
          mixedMode={state.mixedMode}
          config={state.mixedModeConfig}
          onToggleAllRandom={onToggleAllRandom}
          onConfigChange={onMixedModeConfigChange}
          onMixedModeChange={onMixedModeChange}
          isPro={isPro}
          canUseMixedModeConfig={canUseMixedModeConfig}
        />
      </CollapsibleSection>
    </div>
  );
}
